import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
} from "@angular/core";
import { StateService } from "../../services/state.service";
import { HttpService } from "../../services/http.service";
import { MessageService } from "primeng/api";
import { CatExpertRouterLink } from "../../app-routing-enum";
import { cloneDeep } from "lodash";
import {
  Breadcrumb,
  experimentListTableColumn,
  keysToSelectedFolder,
  newFolderDialogContent,
} from "../../main-container/studio/studio-constants";

@Component({
  selector: "app-move-experiment-folder",
  templateUrl: "./move-experiment-folder.component.html",
  styleUrl: "./move-experiment-folder.component.scss",
})
export class MoveExperimentFolderComponent {
  @Input() moveDialogContent: any = {};
  @Output() moveDialogActionCall = new EventEmitter<any>();
  @Output() moveDialogCreateFolderCall = new EventEmitter<any>();
  @Input() experimentList: any[] = [];
  dialogTableColumns = cloneDeep(experimentListTableColumn);
  parentId: string = "root";
  currentTab: string = CatExpertRouterLink.Experiments;

  currentPage: number = 1;
  pageSize: number = 20;
  totalPages: number = 1;
  totalRecords: number = 0;
  searchText: any = null;

  keysToSelectedFolder = keysToSelectedFolder;
  currentRootFolderData: any = {};

  newFolderDialogContent: any = newFolderDialogContent;

  folderBreadcrumbMap = new Map<string, Breadcrumb>();

  constructor(
    private httpService: HttpService,
    public stateService: StateService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes) {
      this.dialogTabChange(this.currentTab);
      this.dialogTableColumns.shift();
      this.dialogTableColumns.pop();
      this.moveDialogContent = changes.moveDialogContent.currentValue;
    }
  }

  dialogTabChange(value: any) {
    this.currentTab = value;
    this.parentId = "root";
    this.folderBreadcrumbMap = new Map<string, Breadcrumb>();
    if (this.currentTab === CatExpertRouterLink.Experiments) {
      this.handleFoldersExperimentsApiCall();
    } else {
      this.handleSharedFoldersExperimentsApiCall();
    }
  }

  handleFoldersExperimentsApiCall() {
    this.setFolderLocation();
    this.prepareMyExperimentsFoldersCall();
  }

  prepareMyExperimentsFoldersCall() {
    let apiUrl = `${this.stateService.apiList.experiments.getMyFoldersExperiments(this.parentId)}`;
    this.getFoldersExperimentsApiCall(apiUrl);
  }

  handleSharedFoldersExperimentsApiCall() {
    this.setFolderLocation();
    if (this.parentId === "root") {
      this.prepareSharedExperimentsFoldersCall();
    } else {
      this.prepareMyExperimentsFoldersCall();
    }
  }

  prepareSharedExperimentsFoldersCall() {
    let apiUrl = `${this.stateService.apiList.experiments.getSharedFoldersExperiments()}`;
    this.getFoldersExperimentsApiCall(apiUrl);
  }

  setFolderLocation() {
    if (this.currentTab === CatExpertRouterLink.Experiments) {
      const rootBreadcrumb: Breadcrumb = {
        label: "My Experiments",
        folder_id: "root",
      };
      this.folderBreadcrumbMap.set("root", rootBreadcrumb);
    } else {
      const sharedBreadcrumb: Breadcrumb = {
        label: "Shared",
        folder_id: "root",
      };
      this.folderBreadcrumbMap.set("root", sharedBreadcrumb);
    }
  }

  getFoldersExperimentsApiCall(apiUrl: string) {
    let payload: any = {
      page: this.currentPage,
      page_size: this.pageSize,
      search_text: this.searchText,
    };

    this.commonApiCall(
      apiUrl,
      "postMethod",
      payload,
      (response: any, params: any) => {
        this.prepareCurrentRootFolder(response);

        if (response?.full_path) {
          this.prepareFolderLocation(response);
        }

        this.preparePaginationData(response);

        let experiments: any[] = [];
        response?.folders &&
          response?.folders.map((item: any) => {
            item["experiment_type"] = "folder";
          });
        experiments = [
          ...(response.folders || []),
          ...(response.experiments || []),
        ];

        this.experimentList = experiments.map((item: any) => ({
          ...item,
          isEditable: false,
          isLoading: false,
        }));
      }
    );
  }

  // Helper method to call the actions method
  getTableAction(event: {
    action: { callBack: keyof MoveExperimentFolderComponent };
    data: any;
  }) {
    const callback = event.action.callBack;
    if (
      typeof callback === "string" &&
      typeof (this as any)[callback] === "function"
    ) {
      (this as any)[callback](event);
    }
  }

  // Helper method to prepare the folder location(Breadcrumb)
  prepareFolderLocation(response: any) {
    let folderParts = response.full_path.split("/").slice(1);
    let folderIds = response.ancestor_ids.slice(1);
    if (this.currentTab === CatExpertRouterLink.Shared) {
      folderParts = response.access_path.split("/");
      folderIds = response.ancestor_ids.slice(-folderParts.length);
    }

    if (folderIds.length !== folderParts.length) return;

    let maxIndex = folderIds.length;
    if (this.parentId !== "root") {
      const idx = folderIds.indexOf(this.parentId);
      if (idx !== -1) {
        maxIndex = idx + 1;
      }
    }

    // Trim the map - remove entries after the clicked breadcrumb
    const allKeys = Array.from(this.folderBreadcrumbMap.keys());

    const parentIndexInMap = allKeys.indexOf(this.parentId);
    if (parentIndexInMap !== -1) {
      for (let i = parentIndexInMap + 1; i < allKeys.length; i++) {
        this.folderBreadcrumbMap.delete(allKeys[i]);
      }
    }

    // Add new breadcrumb items up to maxIndex
    for (let i = 0; i < maxIndex; i++) {
      const folderId = folderIds[i];

      // Avoid overwriting existing ones
      if (!this.folderBreadcrumbMap.has(folderId)) {
        const breadcrumb: Breadcrumb = {
          label: folderParts[i],
          folder_id: folderId,
        };

        this.folderBreadcrumbMap.set(folderId, breadcrumb);
      }
    }
  }

  // Helper method get the breadcrumb label
  getBreadcrumbList() {
    return Array.from(this.folderBreadcrumbMap.values());
  }

  // Helper method to Current Root Folder Location
  prepareCurrentRootFolder(response: any) {
    this.currentRootFolderData = {};
    if (
      this.parentId === "root" &&
      this.currentTab === CatExpertRouterLink.Shared
    ) {
      return;
    }

    this.currentRootFolderData = Object.fromEntries(
      this.keysToSelectedFolder.map((key) => [key, response[key]])
    );

    this.currentRootFolderData["experiment_type"] = "folder";
  }

  // Helper method to prepare the Pagination data
  preparePaginationData(response: any) {
    this.currentPage = response.page;
    this.pageSize = response.page_size;
    this.totalPages = response.total_pages;
    this.totalRecords = response?.total
      ? response.total
      : response.total_experiments + response.total_folders;
  }

  // Helper method to call the pagination action
  paginationAction(event: any) {
    this.currentPage = event.data.page;
    this.pageSize = event.data.page_size;
    this.filterAndPaginationActionCall();
  }

  filterSearch(value: string) {
    this.searchText = value || null;
    this.filterAndPaginationActionCall();
  }

  filterAndPaginationActionCall() {
    if (
      this.parentId === "root" &&
      this.currentTab === CatExpertRouterLink.Shared
    ) {
      this.prepareSharedExperimentsFoldersCall();
      return;
    }
    this.prepareMyExperimentsFoldersCall();
  }

  navigateExperiment(event: any) {
    let experiment = event.data;
    if (experiment.experiment_type === "folder") {
      this.parentId = experiment.id;
      this.handleFoldersExperimentsApiCall();
      return;
    }
  }

  // Method to navigate the parent path folder from breadcrumb
  navigateToFolderLink(breadcrumb: any) {
    this.parentId = breadcrumb.folder_id;
    if (
      this.parentId === "root" &&
      this.currentTab === CatExpertRouterLink.Shared
    ) {
      this.folderBreadcrumbMap = new Map<string, Breadcrumb>();
      this.handleSharedFoldersExperimentsApiCall();
      return;
    }
    this.handleFoldersExperimentsApiCall();
  }

  // Experiments/Folders Move action
  experimentsFoldersMoveAction(btnFlag: boolean = false) {
    let data = {
      actionValue: btnFlag,
      hasMultipleFlag: this.moveDialogContent.hasMultiple,
      selectedMoveData: this.moveDialogContent.selectedRowData,
      selectedTargetData: this.currentRootFolderData,
    };
    this.moveDialogActionCall.emit(data);
  }

  // Helper method to show the new folder dialog
  createNewFolderDialog() {
    const folderLocationText = this.getBreadcrumbList()
      .map((item: Breadcrumb) => item.label)
      .join(" > ");
    this.newFolderDialogContent.folderLocation = folderLocationText;
    this.newFolderDialogContent.showFolderDialog = true;
  }

  // Helper method to call the folder dialog action
  folderDialogAction(event: any) {
    if (event.actionValue) {
      this.createNewFolderApiCall(event);
    }
    this.newFolderDialogContent.showFolderDialog = false;
  }

  // Method to call create new folder Api call
  createNewFolderApiCall(data: any) {
    let payload = data.formData;
    payload["parent_id"] = this.parentId !== "root" ? this.parentId : null;

    const apiUrl = `${this.stateService.apiList.experiments.createNewFolder}`;

    this.commonApiCall(
      apiUrl,
      "postMethod",
      payload,
      (response: any, params: any) => {
        if (response) {
          let initialExpData = response;
          initialExpData["experiment_type"] = "folder";
          this.experimentList.splice(0, 0, initialExpData);

          // if the parentId is root then emit the event to add the new folder in the top of the list
          if (this.parentId === "root") {
            let data = {
              actionValue: "create",
              parentId: this.parentId,
              folderData: initialExpData,
            };
            this.moveDialogCreateFolderCall.emit(data);
          }
        }
      }
    );
  }

  get showNewFolderButton(): boolean {
    return (
      this.currentTab !== CatExpertRouterLink.Shared || this.parentId !== "root"
    );
  }

  get isMoveButtonDisabled(): boolean {
    // Disable move when on shared tab and at root
    if (
      this.currentTab === CatExpertRouterLink.Shared &&
      this.parentId === "root"
    ) {
      return false;
    }

    const data = this.moveDialogContent.selectedRowData;
    const rootId = this.currentRootFolderData.id;

    // Single item case
    if (!this.moveDialogContent.hasMultiple) {
      const targetId =
        data?.folder_id ??
        data?.parent_id ??
        this.experimentList.find((item) => item.id === data.id)?.folder_id;
      return targetId !== rootId;
    }

    // Multiple item case
    return (
      Array.isArray(data) &&
      data.every((item: any) => {
        const targetId =
          item?.folder_id ??
          item?.parent_id ??
          this.experimentList.find((exp) => exp.id === item.id)?.folder_id;
        return targetId !== rootId;
      })
    );
  }

  // Function for Common API Call
  commonApiCall(
    apiUrl: string,
    method: string,
    params: any,
    callback: (response: any, params: any) => void
  ) {
    this.stateService.addCalledApi(apiUrl);
    this.httpService[method as keyof HttpService](apiUrl, params).subscribe(
      (response: any) => {
        this.stateService.removeCalledApi(apiUrl);
        // common callback function for response
        callback(response, params);
      },
      (err: any) => {
        this.messageService.clear();
        if (err.status === 400) {
          this.messageService.add({
            key: "w",
            severity: "warn",
            summary: "Something went wrong!",
            detail: err.error.message,
            sticky: true,
          });
        } else if (err.status === 422) {
          this.messageService.add({
            key: "w",
            severity: "warn",
            summary: err.error.detail[0].msg || "Something went wrong!",
            detail: err.error.detail,
            sticky: true,
          });
        } else if (err.status !== 401) {
          this.messageService.add({
            key: "c",
            severity: "warn",
            summary: err.error?.message || "Something went wrong!",
            detail: err?.error?.traceback,
            sticky: true,
          });
        }
        this.stateService.removeCalledApi(apiUrl);
      }
    );
  }

  ngOnDestroy() {
    this.dialogTableColumns.unshift();
  }
}
